import { useEffect, useRef } from 'react';
import { Player } from '@brightcove/web-sdk/ui';
import logger from '~/services/logger';
import { BrightcoveDataCollector } from '~/components/player/plugins/brightcove-analytics/brightcove-data-collector';

const useBrightcoveAnalytics = (
  player: Player | null,
  pluginsData: any,
  playableItem: any,
  trackData: any
) => {
  const dataCollectorRef = useRef<BrightcoveDataCollector | null>(null);
  const playRequestStartTimeRef = useRef<number>(0);
  const loadStartTimeRef = useRef<number>(0);
  const rebufferingStartTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!player || !pluginsData?.brightcove_account_id) {
      return;
    }

    const brightcoveVideoId = playableItem?.extensions?.brightcove?.video_id;

    const videoData = {
      id:
        brightcoveVideoId ||
        playableItem?.id ||
        trackData?.media_id ||
        'unknown',
      name: playableItem?.title || trackData?.name || 'Unknown Video',
      duration: 0,
    };

    const initializeCollector = () => {
      dataCollectorRef.current = new BrightcoveDataCollector({
        accountId: pluginsData.brightcove_account_id,
        playerId: 'brightcove-player',
        playerName: pluginsData.brightcoveAnalytics?.player_name,
        userId: pluginsData?.brightcoveUserId,
        enableQoE: pluginsData.brightcoveAnalytics?.enable_qoe,
        engagementUpdateInterval:
          pluginsData.brightcoveAnalytics?.engagement_update_interval,
      });

      dataCollectorRef.current.trackPlayerLoad();
    };

    const onLoadStart = () => {
      loadStartTimeRef.current = Date.now();

      if (!dataCollectorRef.current) {
        initializeCollector();
      }
    };

    const onLoadedMetadata = async () => {
      const duration = player.getDuration();
      if (duration && !isNaN(duration)) {
        videoData.duration = Math.round(duration);
      }

      if (dataCollectorRef.current) {
        await dataCollectorRef.current.trackVideoImpression(videoData);
      }
    };

    const onPlay = async () => {
      if (!dataCollectorRef.current) {
        logger.error(
          'BrightcoveAnalytics: DataCollector not initialized during play'
        );
        return;
      }

      playRequestStartTimeRef.current = Date.now();

      // Track play request
      await dataCollectorRef.current.trackPlayRequest(videoData);
    };

    const onPlaying = async () => {
      if (!dataCollectorRef.current) return;

      const now = Date.now();
      const loadTimeMs = loadStartTimeRef.current
        ? now - loadStartTimeRef.current
        : undefined;
      const startTimeMs = playRequestStartTimeRef.current
        ? now - playRequestStartTimeRef.current
        : undefined;

      await dataCollectorRef.current.trackVideoView(
        videoData,
        loadTimeMs,
        startTimeMs
      );

      dataCollectorRef.current.startEngagementTracking(
        () => player.getCurrentTime() || 0,
        videoData
      );
    };

    const onTimeUpdate = () => {
      // Update video data with current duration if it changed (for live streams)
      const duration = player.getDuration();
      if (duration && !isNaN(duration) && duration !== videoData.duration) {
        videoData.duration = Math.round(duration);
      }
    };

    const onPause = () => {
      if (dataCollectorRef.current) {
        dataCollectorRef.current.stopEngagementTracking();
      }
    };

    const onSeeking = () => {
      if (dataCollectorRef.current) {
        dataCollectorRef.current.stopEngagementTracking();
      }
    };

    const onSeeked = () => {
      if (dataCollectorRef.current) {
        dataCollectorRef.current.startEngagementTracking(
          () => player.getCurrentTime() || 0,
          videoData
        );
      }
    };

    const onWaiting = () => {
      rebufferingStartTimeRef.current = Date.now();
      if (dataCollectorRef.current) {
        dataCollectorRef.current.updateQoEData(undefined, 1); // Increment rebuffering count
      }
    };

    const onCanPlay = () => {
      if (rebufferingStartTimeRef.current) {
        const rebufferingDuration =
          (Date.now() - rebufferingStartTimeRef.current) / 1000;

        if (dataCollectorRef.current) {
          dataCollectorRef.current.updateQoEData(rebufferingDuration);
        }

        rebufferingStartTimeRef.current = null;
      }
    };

    const onError = async () => {
      if (!dataCollectorRef.current) {
        initializeCollector();
      }

      const errorCode = 'unknown_error'; // Brightcove SDK doesn't expose error codes the same way

      await dataCollectorRef.current?.trackError(errorCode, videoData);

      logger.error(`BrightcoveAnalytics: Error tracked - ${errorCode}`);
    };

    const onAdStart = async () => {
      if (dataCollectorRef.current) {
        await dataCollectorRef.current.trackAdModeBegin();
        dataCollectorRef.current.stopEngagementTracking();
      }
    };

    const onAdEnd = async () => {
      if (dataCollectorRef.current) {
        await dataCollectorRef.current.trackAdModeComplete();
        dataCollectorRef.current.startEngagementTracking(
          () => player.getCurrentTime() || 0,
          videoData
        );
      }
    };

    const onEnded = () => {
      if (dataCollectorRef.current) {
        dataCollectorRef.current.stopEngagementTracking();
      }
    };

    const onDispose = () => {
      if (dataCollectorRef.current) {
        dataCollectorRef.current.dispose();
      }
    };

    // Add event listeners
    player.addEventListener(Player.Event.PlayerLoadStart, onLoadStart);
    player.addEventListener(
      Player.Event.PlayerLoadedMetadata,
      onLoadedMetadata
    );
    player.addEventListener(Player.Event.PlayerPlay, onPlay);
    player.addEventListener(Player.Event.PlayerPlaying, onPlaying);
    player.addEventListener(Player.Event.PlayerTimeChanged, onTimeUpdate);
    player.addEventListener(Player.Event.PlayerPause, onPause);
    player.addEventListener(Player.Event.PlayerSeeking, onSeeking);
    player.addEventListener(Player.Event.PlayerSeeked, onSeeked);
    player.addEventListener(Player.Event.PlayerWaiting, onWaiting);
    player.addEventListener(Player.Event.PlayerCanPlay, onCanPlay);
    player.addEventListener(Player.Event.PlayerError, onError);
    player.addEventListener(Player.Event.PlayerEnded, onEnded);
    player.addEventListener(Player.Event.PlayerDisposed, onDispose);

    // Ad events (if available) - using string events as they might not be in the enum
    try {
      player.addEventListener('adstart' as any, onAdStart);
      player.addEventListener('adend' as any, onAdEnd);
    } catch (error) {
      // Ad events might not be available
      logger.info('BrightcoveAnalytics: Ad events not available');
    }

    return () => {
      if (dataCollectorRef.current) {
        dataCollectorRef.current.dispose();
      }

      try {
        player.removeEventListener(Player.Event.PlayerLoadStart, onLoadStart);
        player.removeEventListener(
          Player.Event.PlayerLoadedMetadata,
          onLoadedMetadata
        );
        player.removeEventListener(Player.Event.PlayerPlay, onPlay);
        player.removeEventListener(Player.Event.PlayerPlaying, onPlaying);
        player.removeEventListener(
          Player.Event.PlayerTimeChanged,
          onTimeUpdate
        );
        player.removeEventListener(Player.Event.PlayerPause, onPause);
        player.removeEventListener(Player.Event.PlayerSeeking, onSeeking);
        player.removeEventListener(Player.Event.PlayerSeeked, onSeeked);
        player.removeEventListener(Player.Event.PlayerWaiting, onWaiting);
        player.removeEventListener(Player.Event.PlayerCanPlay, onCanPlay);
        player.removeEventListener(Player.Event.PlayerError, onError);
        player.removeEventListener(Player.Event.PlayerEnded, onEnded);
        player.removeEventListener(Player.Event.PlayerDisposed, onDispose);

        player.removeEventListener('adstart' as any, onAdStart);
        player.removeEventListener('adend' as any, onAdEnd);
      } catch (error) {
        logger.error('BrightcoveAnalytics: Error removing event listeners');
      }
    };
  }, [player, pluginsData, playableItem, trackData]);
};

export default useBrightcoveAnalytics;
